import { useState, useEffect } from "react";
import {
  CreditCard,
  Plus,
  Trash2,
  ExternalLink,
  Shield,
  CheckCircle,
} from "lucide-react";
import { PageNavigation } from "../components/ui/page-navigation";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";

export default function PaymentMethodManagement() {
  const { toast } = useToast();

  useEffect(() => {
    document.title = "Payment Methods - Seismic Connect";
  }, []);

  const [savedCards, setSavedCards] = useState([
    {
      id: 1,
      type: "Visa",
      last4: "4242",
      expiry: "12/25",
      isDefault: true,
    },
    {
      id: 2,
      type: "Mastercard",
      last4: "5555",
      expiry: "08/26",
      isDefault: false,
    },
  ]);

  const [isVenmoLinked, setIsVenmoLinked] = useState(true);

  // Modal states
  const [isAddCardOpen, setIsAddCardOpen] = useState(false);
  const [isRemoveCardOpen, setIsRemoveCardOpen] = useState(false);
  const [isUnlinkVenmoOpen, setIsUnlinkVenmoOpen] = useState(false);
  const [cardToRemove, setCardToRemove] = useState(null);

  // New card form state
  const [newCard, setNewCard] = useState({
    name: "",
    number: "",
    expiry: "",
    cvv: "",
  });

  const handleAddCard = () => {
    if (!newCard.number || newCard.number.length < 15) {
      toast({ title: "Invalid Card", description: "Please enter a valid card number.", variant: "destructive" });
      return;
    }
    const last4 = newCard.number.slice(-4);
    const id = Date.now();
    setSavedCards([
      ...savedCards,
      {
        id,
        type: newCard.number.startsWith("4") ? "Visa" : "Mastercard",
        last4,
        expiry: newCard.expiry,
        isDefault: savedCards.length === 0,
      },
    ]);
    setNewCard({ name: "", number: "", expiry: "", cvv: "" });
    setIsAddCardOpen(false);
    toast({ title: "Card Added", description: "Your new payment method has been saved." });
  };

  const confirmRemoveCard = (card) => {
    setCardToRemove(card);
    setIsRemoveCardOpen(true);
  };

  const handleRemoveCard = () => {
    setSavedCards(savedCards.filter((c) => c.id !== cardToRemove.id));
    setIsRemoveCardOpen(false);
    setCardToRemove(null);
    toast({ title: "Card Removed", description: "The payment method has been deleted." });
  };

  const handleSetDefault = (id) => {
    setSavedCards(
      savedCards.map((c) => ({
        ...c,
        isDefault: c.id === id,
      }))
    );
    toast({ title: "Default Updated", description: "Your default payment method has been updated." });
  };

  const handleUnlinkVenmo = () => {
    setIsVenmoLinked(false);
    setIsUnlinkVenmoOpen(false);
    toast({ title: "Venmo Unlinked", description: "Your Venmo Business account has been unlinked." });
  };

  const handleLinkVenmo = () => {
    setIsVenmoLinked(true);
    toast({ title: "Venmo Linked", description: "Your Venmo Business account is now linked." });
  };

  return (
    <div className="px-4 pb-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <PageNavigation
          title="Payment Methods"
          subtitle="Manage your saved cards and linked accounts for billing"
        />

        <div className="grid grid-cols-1 gap-6" data-tour="payment-methods-management">
          {/* Credit Cards Section */}
          <Card className="shadow-md border border-gray-100" data-tour="payment-methods-cards">
            <CardHeader className="flex flex-row items-center justify-between pb-4 border-b border-gray-100">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  Credit & Debit Cards
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Manage your saved cards for recurring subscriptions.
                </p>
              </div>
              <Button onClick={() => setIsAddCardOpen(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="w-4 h-4" /> Add Card
              </Button>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {savedCards.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No saved cards found. Add a new card to continue.
                </div>
              ) : (
                savedCards.map((card) => (
                  <div
                    key={card.id}
                    className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition"
                  >
                    <div className="flex items-center gap-4 mb-3 sm:mb-0">
                      <div className="w-12 h-8 bg-white border border-gray-200 rounded shadow-sm flex items-center justify-center font-bold text-blue-800 italic text-sm">
                        {card.type}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 flex items-center gap-2">
                          •••• •••• •••• {card.last4}
                          {card.isDefault && (
                            <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded uppercase font-bold tracking-wider">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          Expires {card.expiry}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                      {!card.isDefault && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSetDefault(card.id)}
                          className="flex-1 sm:flex-none text-xs"
                        >
                          Make Default
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => confirmRemoveCard(card)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-1 sm:flex-none"
                      >
                        <Trash2 className="w-4 h-4 sm:mr-0 mr-2" />
                        <span className="sm:hidden">Remove</span>
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Venmo Business Integration */}
          <Card className="shadow-md border border-gray-100" data-tour="payment-methods-venmo">
            <CardHeader className="flex flex-row items-center justify-between pb-4 border-b border-gray-100">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <span className="text-xl">📱</span>
                  Venmo Business
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Link your Venmo Business account for streamlined payments.
                </p>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {isVenmoLinked ? (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 border-2 border-emerald-100 bg-emerald-50 rounded-lg">
                  <div className="flex items-center gap-3 mb-4 sm:mb-0">
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                    <div>
                      <div className="font-medium text-emerald-900">
                        Venmo Business Linked
                      </div>
                      <div className="text-sm text-emerald-700">
                        Connected to @seismic-health
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setIsUnlinkVenmoOpen(true)}
                    className="border-emerald-200 text-emerald-800 hover:bg-emerald-100 w-full sm:w-auto"
                  >
                    Unlink Account
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-200 rounded-lg text-center">
                  <span className="text-4xl mb-3">📱</span>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    Connect Venmo
                  </h3>
                  <p className="text-gray-500 text-sm max-w-sm mb-5">
                    Connect your Venmo Business profile to quickly pay for add-ons and services.
                  </p>
                  <Button onClick={handleLinkVenmo} className="bg-[#008CFF] hover:bg-[#0074D6] text-white">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Link Venmo Profile
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Security Note */}
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-4">
            <Shield className="w-4 h-4 text-gray-400" />
            Your payment information is encrypted and securely stored.
          </div>
        </div>

        {/* Modals */}
        <Dialog open={isAddCardOpen} onOpenChange={setIsAddCardOpen}>
          <DialogContent className="sm:max-w-[450px] p-6 bg-white rounded-xl shadow-2xl border-0">
            <DialogHeader className="pb-4 border-b border-gray-100">
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                Add New Card
              </DialogTitle>
              <DialogDescription className="text-gray-500 pt-1">
                Enter your card details below. Your information is securely encrypted.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-5 py-5">
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-gray-700">Cardholder Name</Label>
                <Input
                  className="h-11 border-gray-200 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                  placeholder="e.g. John Doe"
                  value={newCard.name}
                  onChange={(e) => setNewCard({ ...newCard, name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-gray-700">Card Number</Label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    className="h-11 pl-10 border-gray-200 focus:ring-blue-500 focus:border-blue-500 shadow-sm font-mono"
                    placeholder="0000 0000 0000 0000"
                    maxLength={19}
                    value={newCard.number}
                    onChange={(e) => setNewCard({ ...newCard, number: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-gray-700">Expiry Date</Label>
                  <Input
                    className="h-11 border-gray-200 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                    placeholder="MM/YY"
                    maxLength={5}
                    value={newCard.expiry}
                    onChange={(e) => setNewCard({ ...newCard, expiry: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-gray-700">CVV</Label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      className="h-11 pl-10 border-gray-200 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                      placeholder="123"
                      maxLength={4}
                      value={newCard.cvv}
                      onChange={(e) => setNewCard({ ...newCard, cvv: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="pt-4 border-t border-gray-100 flex justify-end gap-3 w-full">
              <Button variant="outline" onClick={() => setIsAddCardOpen(false)} className="h-10 px-6 font-medium border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900">
                Cancel
              </Button>
              <Button onClick={handleAddCard} className="h-10 px-6 font-medium bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-colors">
                Save Card
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isRemoveCardOpen} onOpenChange={setIsRemoveCardOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Remove Payment Method</DialogTitle>
              <DialogDescription>
                Are you sure you want to remove the card ending in {cardToRemove?.last4}?
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setIsRemoveCardOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleRemoveCard} className="bg-red-600 hover:bg-red-700 text-white">
                Remove Card
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isUnlinkVenmoOpen} onOpenChange={setIsUnlinkVenmoOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Unlink Venmo Account</DialogTitle>
              <DialogDescription>
                Are you sure you want to unlink your Venmo Business account?
                You will need to reconnect it or use a credit card for future payments.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setIsUnlinkVenmoOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleUnlinkVenmo} className="bg-red-600 hover:bg-red-700 text-white">
                Unlink Account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
